import React from "react";
import { useForm } from "react-hook-form";
import {
    ensure,
    Form,
    FormActions,
    FormFields,
    FormTextField,
    Spinner,
    Stack,
    WarningMessage
} from "sprint-common-web";

import { CourseOfActionReference } from "../../../common/types/CourseOfActionReference";
import { FixedBillingReference } from "../../../common/types/FixedBillingReferfence";
import { CourseOfActionFormFields } from "../course-of-action/CourseOfActionFormFields";
import { useCourseOfActionFormFieldsValues } from "../course-of-action/useCourseOfActionFormFieldsValues";

export interface WaitingForClientResponseDetailsFormInputs {
    courseOfActionId: string;
    fixedBillingId?: string;
    details: string;
}

export type WaitingForClientResponseDetailsFormProps = {
    onConfirm: ({
        courseOfAction,
        fixedBilling,
        details
    }: {
        details: string;
        courseOfAction: CourseOfActionReference;
        fixedBilling?: FixedBillingReference;
    }) => Promise<void>;
    onCancel: () => void;
};

export const WaitingForClientResponseDetailsForm = ({
    onConfirm,
    onCancel
}: WaitingForClientResponseDetailsFormProps): JSX.Element => {
    const { control, errors, handleSubmit, formState, reset } = useForm<WaitingForClientResponseDetailsFormInputs>();
    const {
        courseOfActions,
        fixedBillings,
        areCourseOfActionsLoading,
        areFixedBillingsLoading
    } = useCourseOfActionFormFieldsValues();

    const handleCancel = () => {
        onCancel();
        reset();
    };

    const handleSubmitButtonClick = async (inputs: WaitingForClientResponseDetailsFormInputs) => {
        const courseOfAction = courseOfActions.find((action) => action.id === inputs.courseOfActionId);
        const fixedBilling = inputs.fixedBillingId
            ? fixedBillings.find((billing) => billing.id === inputs.fixedBillingId)
            : undefined;

        ensure(courseOfAction);

        return onConfirm({
            courseOfAction,
            fixedBilling,
            details: inputs.details
        }).then(() => reset());
    };

    if (areCourseOfActionsLoading || areFixedBillingsLoading) {
        return (
            <Stack
                styles={{
                    root: { padding: 10 }
                }}
            >
                <Spinner />
            </Stack>
        );
    }

    return (
        <Stack>
            <Form onSubmit={handleSubmit(handleSubmitButtonClick)}>
                <FormFields>
                    <CourseOfActionFormFields
                        control={control}
                        errors={errors}
                        courseOfActions={courseOfActions}
                        fixedBillings={fixedBillings}
                    />
                    <FormTextField
                        control={control}
                        label="Status details"
                        name="details"
                        multiline={true}
                        errors={errors}
                    />
                </FormFields>
                <WarningMessage
                    message="If a file record is linked to the cloud file, it will also be updated to Waiting for Client Response."
                    styles={{ root: { marginTop: 10 } }}
                    testId="linked-file-record-warning"
                />
                <FormActions
                    isLoading={formState.isSubmitting}
                    submitDisabled={areCourseOfActionsLoading || areFixedBillingsLoading}
                    onCancel={handleCancel}
                />
            </Form>
        </Stack>
    );
};
